/**
 * Bag photos are downsized and re-encoded in the browser before upload: labels stay readable, uploads stay small. The
 * server makes the small square thumbnail the app shows in lists (src/server/thumbnails.ts). Opaque photos (every
 * camera photo) become JPEGs. Transparent ones, such as a roaster's cut-out of the bag, keep their alpha: WebP, or PNG
 * where the browser can't write WebP (Safari).
 */
import { BEAN_PHOTO_MAX_BYTES, type BeanPhotoType } from '@shared/schemas';

/** Longest edge after downsizing. Enough to read the small print on a label when zoomed in. */
export const PHOTO_MAX_EDGE_PX = 2048;
/** Longest edge of the copy the alpha check reads. */
const ALPHA_PROBE_PX = 64;

/**
 * How the full photo is encoded in each type: the longest edge to start from, and the qualities tried in turn until it
 * fits BEAN_PHOTO_MAX_BYTES (a busy, detailed photo can need a lower one). PNG ignores quality, so it only steps down
 * in size, and starts lower: a photo-like PNG at 2048 px is about twice the limit, and the iPad is slow to encode one.
 */
const FULL_ATTEMPTS: Record<BeanPhotoType, { maxEdge: number; qualities: (number | undefined)[] }> = {
  'image/jpeg': { maxEdge: PHOTO_MAX_EDGE_PX, qualities: [0.85, 0.75, 0.65] },
  'image/webp': { maxEdge: PHOTO_MAX_EDGE_PX, qualities: [0.85, 0.75] },
  'image/png': { maxEdge: 1536, qualities: [undefined] },
};

const EXTENSIONS: Record<BeanPhotoType, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

/** A photo ready to upload: downsized and re-encoded. The server makes its thumbnail. */
export interface PreparedPhoto {
  full: Blob;
}

/** Where a photo shows: its thumbnail in the small squares, the full image in the viewer. */
export interface PhotoSources {
  thumb: string;
  full: string;
}

/** Width and height scaled to fit within maxEdge, keeping the aspect ratio. Never upscales. */
export function fitWithin(width: number, height: number, maxEdge: number = PHOTO_MAX_EDGE_PX): { width: number; height: number } {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** True when any pixel of RGBA data (as from getImageData) isn't fully opaque. */
export function hasAlpha(rgba: ArrayLike<number>): boolean {
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i]! < 255) return true;
  return false;
}

/** A file name for an upload part, after the blob's type: `photo.webp`. */
export function photoFileName(name: string, blob: Blob): string {
  return `${name}.${EXTENSIONS[blob.type as BeanPhotoType] ?? 'jpg'}`;
}

/** What to draw: the photo scaled to width x height. */
export interface EncodeTarget {
  width: number;
  height: number;
}
/** Draws a target and encodes it as `type`. The blob's type is what the browser wrote: PNG for a type it can't write. */
export type Encode = (target: EncodeTarget, type: BeanPhotoType, quality?: number) => Promise<Blob>;

/** The sizes and qualities tried for `type`, in turn. */
function* attempts(type: BeanPhotoType, width: number, height: number): Generator<{ target: EncodeTarget; quality: number | undefined }> {
  const { maxEdge: startEdge, qualities } = FULL_ATTEMPTS[type];
  for (let maxEdge = startEdge; maxEdge >= 512; maxEdge = Math.round(maxEdge * 0.75)) {
    const target = fitWithin(width, height, maxEdge);
    for (const quality of qualities) yield { target, quality };
  }
}

/**
 * Encodes a width x height photo under BEAN_PHOTO_MAX_BYTES. Opaque photos become JPEGs; transparent ones WebP, or PNG
 * when the browser writes PNG instead, which the first attempt finds out.
 */
export async function encodePhoto(encode: Encode, width: number, height: number, transparent: boolean): Promise<PreparedPhoto> {
  let type: BeanPhotoType = transparent ? 'image/webp' : 'image/jpeg';
  let tries = attempts(type, width, height);
  for (let next = tries.next(); !next.done; next = tries.next()) {
    const full = await encode(next.value.target, type, next.value.quality);
    if (full.size <= BEAN_PHOTO_MAX_BYTES) return { full };
    // Safari can't write WebP, and toBlob hands back a PNG instead: on with PNG's sizes, which start smaller.
    if (type === 'image/webp' && full.type !== 'image/webp') {
      type = 'image/png';
      tries = attempts(type, width, height);
    }
  }
  throw new Error('That image is too large, even downsized.');
}

/** Whether the image has any pixel that isn't fully opaque, read from a copy at most ALPHA_PROBE_PX across. */
function isTransparent(canvas: HTMLCanvasElement, img: HTMLImageElement): boolean {
  const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, ALPHA_PROBE_PX);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Couldn’t process the image.');
  ctx.drawImage(img, 0, 0, width, height);
  return hasAlpha(ctx.getImageData(0, 0, width, height).data);
}

const shownTransparent = new Map<string, Promise<boolean>>();

/**
 * Whether a photo the app shows has see-through pixels, read from its thumbnail (small, and usually loaded already).
 * Same-origin and blob: URLs only, so the canvas stays readable. One check per URL; false when it can't be read.
 */
export function photoIsTransparent(thumbSrc: string): Promise<boolean> {
  let found = shownTransparent.get(thumbSrc);
  if (!found) {
    found = (async () => {
      const img = new Image();
      img.src = thumbSrc;
      const canvas = document.createElement('canvas');
      try {
        await img.decode();
        return isTransparent(canvas, img);
      } catch {
        return false;
      } finally {
        canvas.width = 0;
        canvas.height = 0;
      }
    })();
    shownTransparent.set(thumbSrc, found);
  }
  return found;
}

function canvasEncoder(canvas: HTMLCanvasElement, img: HTMLImageElement): Encode {
  return async ({ width, height }, type, quality) => {
    // Resizing also clears the canvas to transparent.
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Couldn’t process the image.');
    if (type === 'image/jpeg') {
      // JPEG has no alpha: anything the probe missed would otherwise turn black.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
    }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);
    return new Promise((resolve, reject) =>
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Couldn’t process the image.'))), type, quality),
    );
  };
}

/**
 * Decodes any image the browser can read (HEIC included on Safari), applies its EXIF orientation, downsizes it and
 * re-encodes it under BEAN_PHOTO_MAX_BYTES (see encodePhoto). Re-encoding also drops metadata such as the GPS location.
 */
export async function preparePhoto(file: Blob): Promise<PreparedPhoto> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch {
      throw new Error('Couldn’t read that image. Try a JPEG or PNG.');
    }
    // One canvas for every attempt: iOS Safari caps total canvas memory and only frees it on garbage collection.
    const canvas = document.createElement('canvas');
    try {
      return await encodePhoto(canvasEncoder(canvas, img), img.naturalWidth, img.naturalHeight, isTransparent(canvas, img));
    } finally {
      // Releases its pixels now rather than at the next collection.
      canvas.width = 0;
      canvas.height = 0;
    }
  } finally {
    URL.revokeObjectURL(url);
  }
}
